import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { formatDate } from '../utils/dateFormat';

const US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  email: '',
  medicalNotes: '',
  emergencyInstructions: '',
  timezone: 'America/Chicago',
};

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const { data } = await api.get('/patients', { params });
      setPatients(data.data || data.patients || []);
      setPagination(data.pagination || {});
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/patients', form);
      setForm(EMPTY_FORM);
      setShowForm(false);
      fetchPatients();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add patient');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Patients</h1>
          <p className="text-secondary-500 text-sm mt-1">{pagination.total || 0} patients total</p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium"
        >
          {showForm ? 'Cancel' : '+ Add Patient'}
        </button>
      </div>

      {/* Add Patient Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-card p-5">
          <h2 className="font-semibold text-secondary-900 mb-4">New Patient</h2>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-secondary-500 mb-1">First Name *</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} required
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Last Name *</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} required
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Date of Birth</label>
                <input name="dateOfBirth" type="date" value={form.dateOfBirth} onChange={handleChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Address</label>
                <input name="address" value={form.address} onChange={handleChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">City</label>
                <input name="city" value={form.city} onChange={handleChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">State</label>
                <input name="state" value={form.state} onChange={handleChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Zip Code</label>
                <input name="zipCode" value={form.zipCode} onChange={handleChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Email</label>
                <input name="email" type="email" value={form.email} onChange={handleChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Timezone</label>
                <select name="timezone" value={form.timezone} onChange={handleChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none">
                  {US_TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Medical Notes</label>
                <textarea name="medicalNotes" value={form.medicalNotes} onChange={handleChange} rows={3}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Emergency Instructions</label>
                <textarea name="emergencyInstructions" value={form.emergencyInstructions} onChange={handleChange} rows={3}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none" />
              </div>
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={saving}
                className="px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium disabled:opacity-50">
                {saving ? 'Saving...' : 'Add Patient'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search patients by name, email, phone..."
          className="flex-1 px-4 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Name</th>
                <th className="px-5 py-3 text-left font-medium">DOB</th>
                <th className="px-5 py-3 text-left font-medium">Address</th>
                <th className="px-5 py-3 text-left font-medium">City</th>
                <th className="px-5 py-3 text-left font-medium">Phone</th>
                <th className="px-5 py-3 text-left font-medium">Email</th>
                <th className="px-5 py-3 text-left font-medium"># Devices</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loading && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loading && patients.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-secondary-400">No patients found</td></tr>
              )}
              {!loading && patients.map((p) => (
                <tr key={p.id} className="hover:bg-secondary-50 transition">
                  <td className="px-5 py-3">
                    <Link to={`/patients/${p.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
                      {p.firstName} {p.lastName}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-secondary-700">{formatDate(p.dateOfBirth)}</td>
                  <td className="px-5 py-3 text-secondary-700">{p.address || '--'}</td>
                  <td className="px-5 py-3 text-secondary-700">{p.city || '--'}</td>
                  <td className="px-5 py-3 text-secondary-700">{p.phone || '--'}</td>
                  <td className="px-5 py-3 text-secondary-700">{p.email || '--'}</td>
                  <td className="px-5 py-3 text-secondary-700">{p.deviceCount ?? p.device_count ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-secondary-100 flex items-center justify-between">
            <p className="text-sm text-secondary-500">Page {pagination.page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
